import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import posthog from 'posthog-js';
import { authAPI } from '../services/api';
import { identifyMessaging, resetMessaging } from '../lib/clientMessaging';
import { Sentry } from '../lib/sentry';
import { syncSentryUser } from '../lib/sentryUserSync';
import { readSignupInvite, clearSignupInvite } from '../lib/signupInvite';
import { clearCoachCache, type TourRecordDTO } from '../utils/coachMarks';
import { clearDrafts } from '../lib/composerDraft';

// Stitch every client SDK to the same canonical user id (Mongo _id) the
// server uses: PostHog (funnels join across client + server events),
// Customer.io (in-app messages + page timelines), Intercom (identified
// messenger, identity-verified when the server supplies intercomUserHash).
// Each half no-ops when its config is absent.
const identifyClientAnalytics = (u: User | null) => {
  if (!u) return;
  if (process.env.REACT_APP_POSTHOG_KEY) {
    try {
      posthog.identify(String(u.id), {
        email: u.email,
        name: u.fullName || u.name,
        onboarding_completed: !!u.onboardingCompleted,
        github_connected: !!u.githubConnected,
        company_size: u.companySize,
        role: u.role,
        // COUNT only — which tools someone pays for is their business, not a
        // property we ship to a third-party analytics store. Under its own
        // name: the server identify sends `replace_targets` as the list, and
        // one person property cannot be a number here and an array there.
        replace_targets_count: u.replaceTargets?.length ?? 0,
        is_internal: !!u.isInternal,
        is_paying: !!u.isPaying,
      });
    } catch { /* analytics must never break auth */ }
  }
  identifyMessaging({
    id: u.id,
    email: u.email,
    name: u.fullName || u.name,
    intercomUserHash: u.intercomUserHash,
  });
};

export interface User {
  id: string;
  name: string;
  fullName?: string;
  email: string;
  avatar?: string;
  githubUsername?: string;
  githubConnected: boolean;
  googleConnected?: boolean;
  /** Which provider the account first signed in with (server User.authProvider). */
  authProvider?: 'password' | 'github' | 'google';
  emailVerified: boolean;
  onboardingCompleted: boolean;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  companySize?: string;
  role?: string;
  /** What they typed under "Please specify" for role: 'other'. Read back on
   *  resume so the wizard can refill the box (see getPublicProfile). */
  roleOther?: string;
  industry?: string;
  industryOther?: string;
  intent?: string;
  /** Display names of the paid tools picked at onboarding ("HubSpot"). */
  replaceTargets?: string[];
  /** The resume point set by PATCH /auth/onboarding-progress — the last step
   *  saved before the person left onboarding, if they haven't finished it. */
  onboardingStep?: string;
  /** The subset of replaceTargets the person typed rather than picked from
   *  the catalog ("Xero") — see utils/onboarding.ts's OnboardingData. */
  replaceCustom?: string[];
  settings: {
    defaultBranch: string;
    notifications: {
      email: boolean;
      tasks: boolean;
    };
  };
  githubId?: string;
  // Waitlist gating — `allowed` is read by WaitlistWall and the api.ts 403
  // redirect, not by ProtectedRoute (which only checks `user` and
  // `onboardingCompleted`).
  isInternal?: boolean;
  isPaying?: boolean;
  accessCodeRedeemed?: boolean;
  grandfathered?: boolean;
  allowed?: boolean;
  // A Settings email change waiting for its confirm link (server
  // lib/emailChange.js); `email` stays the old address until it is clicked.
  pendingEmail?: string | null;
  // An admin revoked access: /waitlist shows "access paused", not the wall.
  accessRevoked?: boolean;
  // Ops admin panel access — server-computed (allowlist ∧ @founding.dev,
  // see server/lib/staffAllowlist.js). The client never re-derives it.
  isStaff?: boolean;
  // Connector UI for this account — server-computed (CONNECTORS_OFF + allow-list,
  // see server/lib/connectorsSwitch.js), the same answer the agent gets.
  connectorsEnabled?: boolean;
  customDomainsEnabled?: boolean;
  // The Video build kind for this account — server-computed (server
  // lib/videoConfig.js isVideoEnabledFor, on /auth/me). The home composer's
  // Video tab renders only when it is true.
  videoEnabled?: boolean;
  /** The longest video this account may make, in seconds (server lib/videoScratch.js maxLengthSec). */
  videoMaxSec?: number;
  // The Video composer also takes one video of their own as a reference
  // (server FDEV_VIDEO_UPLOAD_VIDEO); absent: images only.
  videoUploadVideo?: boolean;
  /** HMAC for Intercom identity verification — present on /auth/me responses
   *  when the server has INTERCOM_IDENTITY_SECRET configured. */
  intercomUserHash?: string;
  // Guided tour v2 (lib/coachJourney.ts) — see server/lib/tourFields.js.
  /** Per-tour (and per-nudge, keyed `nudge-<id>`) end-of-run state. */
  tours?: Record<string, TourRecordDTO>;
  /** Armed once at onboarding completion; null on a pre-ship account. */
  journey?: {
    startedAt?: string | null;
    completedAt?: string | null;
    milestones?: { firstApp?: string | null; firstBuild?: string | null; firstPublish?: string | null } | null;
  } | null;
  /** When onboarding finished, if it has. */
  onboardingCompletedAt?: string | null;
  /** Activation signal, NOT a build count — may include plan turns. */
  tasksSucceededCount?: number;
  /** Server-computed: has this account ever published a project. */
  hasPublished?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  loginWithGitHub: () => void;
  loginWithGoogle: () => void;
  handleAuthCallback: (token: string) => Promise<User>;
  refreshUser: () => Promise<User | null>;
  /** Fold a PUT echo (tours/journey) into the current user without a full
   *  /me round trip. No-ops if there is no user (logged out mid-flight). */
  mergeUser: (patch: Partial<User>) => void;
  // Resolves to the server's answer: `emailChange.pending` when a new email
  // is waiting for its confirm link (the email itself is unchanged until then).
  updateProfile: (data: Partial<User>) => Promise<{ emailChange?: { pending: boolean; email: string } }>;
  disconnectGithub: () => Promise<void>;
  completeOnboarding: (data: {
    firstName: string;
    lastName: string;
    companyName?: string;
    companySize: string;
    role: string;
    roleOther?: string;
    industry: string;
    industryOther?: string;
    intent: string;
    replaceTargets?: string[];
    replaceCustom?: string[];
    replaceSearches?: string[];
  }) => Promise<User>;
  /** Fire-and-forget save-as-you-go for onboarding — never throws, so a
   *  Continue/Skip press never blocks or errors on a flaky save. */
  saveOnboardingProgress: (
    data: Partial<{
      firstName: string;
      lastName: string;
      companyName: string;
      companySize: string;
      role: string;
      roleOther: string;
      industry: string;
      industryOther: string;
      intent: string;
      replaceTargets: string[];
      replaceCustom: string[];
      replaceSearches: string[];
    }> & { step: string }
  ) => Promise<void>;
  loading: boolean;
  error: string | null;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: true,
  error: null,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  loginWithGitHub: () => {},
  loginWithGoogle: () => {},
  handleAuthCallback: async () => {
    throw new Error('AuthContext not initialized');
  },
  refreshUser: async () => null,
  mergeUser: () => {},
  updateProfile: async () => ({}),
  disconnectGithub: async () => {},
  completeOnboarding: async () => {
    throw new Error('AuthContext not initialized');
  },
  saveOnboardingProgress: async () => {},
});

/**
 * Whether the connector UI shows for the signed-in account. Connectors are
 * hidden unless the server enables them for this account (profile
 * `connectorsEnabled`). Unlike useAuth it never throws: outside a provider,
 * or before the profile loads, connectors stay hidden.
 */
export const useConnectorsVisible = (): boolean => {
  const context = useContext(AuthContext);
  return !!(context && context.user && context.user.connectorsEnabled);
};

/**
 * Whether the Publish panel offers custom domains: the server's answer for this
 * account (profile `customDomainsEnabled`, server lib/customDomainsSwitch). Hidden
 * outside a provider, before the profile loads, and on servers without the flag.
 */
export const useCustomDomainsVisible = (): boolean => {
  const context = useContext(AuthContext);
  return !!(context && context.user && context.user.customDomainsEnabled);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// /auth/callback with a one-time code (or the older token): the page that
// installs a new session. An ?error= or a bare visit replaces nothing.
const isSessionHandoff = (loc: Location): boolean => {
  if (loc.pathname.replace(/\/+$/, '') !== '/auth/callback') return false;
  const q = new URLSearchParams(loc.search);
  return q.has('code') || q.has('token');
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lane 0: customer context on the client. Fires on every `user` state
  // transition (initial load, login, refresh, logout) regardless of which
  // call site set it, so it can't drift out of sync with a new setUser call
  // added elsewhere in this file. See lib/sentryUserSync.ts.
  useEffect(() => {
    syncSentryUser(Sentry, user);
  }, [user]);

  useEffect(() => {
    const initAuth = async () => {
      // A sign-in callback carrying a session (AuthCallback) is replacing the
      // stored one, so checking the stored one can only race it: a stale token
      // 401s and the global handler sends the person back to /auth mid-sign-in,
      // and another account's valid token can resolve after the new user's.
      if (isSessionHandoff(window.location)) {
        setLoading(false);
        return;
      }
      const storedToken = localStorage.getItem('authToken');
      if (storedToken) {
        setToken(storedToken);
        try {
          const response = await authAPI.me();
          setUser(response.data.user);
          identifyClientAnalytics(response.data.user);
        } catch (error) {
          console.error('Failed to fetch user:', error);
          localStorage.removeItem('authToken');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authAPI.login(email, password);
      const { token: newToken, user: newUser } = response.data;
      
      setToken(newToken);
      setUser(newUser);
      identifyClientAnalytics(newUser);
      localStorage.setItem('authToken', newToken);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authAPI.register(name, email, password);
      const { token: newToken, user: newUser } = response.data;
      
      setToken(newToken);
      setUser(newUser);
      identifyClientAnalytics(newUser);
      localStorage.setItem('authToken', newToken);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Registration failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    // Start a fresh anonymous PostHog identity so the next user isn't stitched
    // to this one on a shared device.
    if (process.env.REACT_APP_POSTHOG_KEY) {
      try { posthog.reset(); } catch { /* never break logout */ }
    }
    // Same for Customer.io + Intercom (Intercom 'shutdown' also clears its
    // conversation cookie).
    resetMessaging();
    // Guided-tour v2 optimistic cache is per-browser, not per-user-in-memory
    // — wipe it so a different account on this device doesn't inherit it.
    clearCoachCache();
    // Same for unsent composer drafts.
    clearDrafts();
  };

  const loginWithGitHub = () => {
    window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/github`;
  };

  const loginWithGoogle = () => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    window.location.href = `${base}/api/google/oauth/authorize`;
  };

  const handleAuthCallback = async (newToken: string): Promise<User> => {
    localStorage.setItem('authToken', newToken);
    setToken(newToken);
    const response = await authAPI.me();
    const fetchedUser = response.data.user;
    setUser(fetchedUser);
    identifyClientAnalytics(fetchedUser);
    return fetchedUser;
  };

  // Re-fetch the current user from the server. Used by the waitlist wall
  // after a successful code redemption so `allowed` flips and the wall's own
  // guard effect (WaitlistWall.tsx) navigates on instead of redirecting back.
  const refreshUser = useCallback(async (): Promise<User | null> => {
    try {
      const response = await authAPI.me();
      const fetched = response.data.user;
      setUser(fetched);
      return fetched;
    } catch (err) {
      console.error('refreshUser failed:', err);
      return null;
    }
  }, []);

  // Signup invite link (?invite=<code>, stashed at boot — lib/signupInvite.ts):
  // redeem it for whoever is signed in, new signup or existing account alike.
  // Runs here rather than in AuthCallback so an already-signed-in user who
  // opens the link is covered too. When it grants access, refresh so
  // `allowed` flips and the waitlist wall lets them through.
  const redeemingInviteRef = useRef(false);
  const userId = user?.id;
  useEffect(() => {
    if (!userId || redeemingInviteRef.current) return;
    const code = readSignupInvite();
    if (!code) return;
    redeemingInviteRef.current = true;
    authAPI
      .redeemInvite(code)
      .then((res) => {
        clearSignupInvite();
        if (res.data.granted) refreshUser();
      })
      .catch((err) => {
        // A definite "no" (unknown / switched-off code) is final; a network
        // error or rate limit keeps the code for the next app load.
        if (err?.response?.status === 400) clearSignupInvite();
      })
      .finally(() => {
        redeemingInviteRef.current = false;
      });
  }, [userId, refreshUser]);

  // Folds a PUT echo (e.g. { tours } from PUT /auth/tours, { journey } from
  // PUT /auth/journey) into the current user in place — lib/coachJourney.ts
  // calls this so a tour/milestone write doesn't need its own /me refetch.
  const mergeUser = useCallback((patch: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const updateProfile = async (data: Partial<User>) => {
    try {
      setError(null);
      const response = await authAPI.updateProfile(data);
      setUser(response.data.user);
      return response.data || {};
    } catch (error: any) {
      setError(error.response?.data?.error || 'Profile update failed');
      throw error;
    }
  };

  const disconnectGithub = useCallback(async () => {
    if (!token) return;
    
    try {
      await authAPI.disconnectGithub();
      
      // Update user state to reflect disconnection
      if (user) {
        setUser({
          ...user,
          githubConnected: false,
          githubId: undefined,
          githubUsername: undefined,
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disconnect GitHub');
      throw err;
    }
  }, [token, user]);

  const completeOnboarding = async (data: {
    firstName: string;
    lastName: string;
    companyName?: string;
    companySize: string;
    role: string;
    roleOther?: string;
    industry: string;
    industryOther?: string;
    intent: string;
    replaceTargets?: string[];
    replaceCustom?: string[];
    replaceSearches?: string[];
  }): Promise<User> => {
    try {
      setError(null);
      const response = await authAPI.completeOnboarding(data);
      setUser(response.data.user);
      return response.data.user as User;
    } catch (error: any) {
      setError(error.response?.data?.error || 'Onboarding completion failed');
      throw error;
    }
  };

  // Save-as-you-go for onboarding — called on every Continue/Skip press.
  // Deliberately swallows its own errors: a flaky save must never surface an
  // error banner or block the wizard from advancing, since completeOnboarding
  // (above) is the real submission and already has its own error handling.
  const saveOnboardingProgress = async (
    data: Partial<{
      firstName: string;
      lastName: string;
      companyName: string;
      companySize: string;
      role: string;
      roleOther: string;
      industry: string;
      industryOther: string;
      intent: string;
      replaceTargets: string[];
      replaceCustom: string[];
      replaceSearches: string[];
    }> & { step: string }
  ): Promise<void> => {
    try {
      const response = await authAPI.saveOnboardingProgress(data);
      setUser((prev) => (prev ? { ...prev, ...response.data.user } : response.data.user));
    } catch (error) {
      console.warn('saveOnboardingProgress failed (non-fatal):', error);
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loginWithGitHub,
    loginWithGoogle,
    handleAuthCallback,
    refreshUser,
    mergeUser,
    updateProfile,
    disconnectGithub,
    completeOnboarding,
    saveOnboardingProgress,
    loading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 